/**
 * Eventos leves do app do cliente, para acionar diálogos de fora do componente
 * que os hospeda (ex.: pelo atalho no menu lateral) sem acoplar os componentes
 * nem precisar de store global. Nasceram no modo tablet (chamar garçom) e hoje
 * servem também o cardápio por celular.
 */
const EVT_ABRIR_CHAMAR_GARCOM = 'tablet:abrir-chamar-garcom';
const EVT_ABRIR_CONTA = 'tablet:abrir-conta';
const EVT_ABRIR_CONFIG = 'dispositivo:abrir-config';
const EVT_ABRIR_PROMOCOES = 'cardapio:abrir-promocoes';

/**
 * Aparência do botão "Chamar garçom" em TODA a aplicação: contornado e amarelo
 * (`warning`). Amarelo porque chamar o garçom é um aviso, não um erro nem a
 * ação principal da tela; contornado para não competir com o "Ver a conta" e o
 * "Fazer pedido", que são as ações que movem o pedido adiante.
 *
 * Fica aqui, e não copiado em cada tela, porque já divergiu uma vez — o do
 * cabeçalho do tablet virou vermelho e o do diálogo da conta virou preenchido.
 * Espalhe com `{...ESTILO_BOTAO_GARCOM}` e deixe `size`/`sx` a cargo da tela.
 */
export const ESTILO_BOTAO_GARCOM = { variant: 'outlined', color: 'warning' } as const;

/**
 * Aparência do botão "Conta" / "Ver a conta" em TODA a aplicação: contornado e
 * verde (`success`). Par do [ESTILO_BOTAO_GARCOM]: as duas são as ações de
 * salão, ficam lado a lado e se distinguem pela cor — amarelo chama gente,
 * verde mostra dinheiro. Contornado pelo mesmo motivo: nenhuma das duas é a
 * ação que move o pedido adiante.
 *
 * Espalhe com `{...ESTILO_BOTAO_CONTA}` e deixe `size`/`sx` a cargo da tela.
 */
export const ESTILO_BOTAO_CONTA = { variant: 'outlined', color: 'success' } as const;

/** Dispara a abertura do diálogo de "chamar garçom". */
export function abrirChamarGarcom(): void {
  window.dispatchEvent(new Event(EVT_ABRIR_CHAMAR_GARCOM));
}

/** Inscreve um callback para abrir o "chamar garçom"; retorna a função de cleanup. */
export function onAbrirChamarGarcom(cb: () => void): () => void {
  window.addEventListener(EVT_ABRIR_CHAMAR_GARCOM, cb);
  return () => window.removeEventListener(EVT_ABRIR_CHAMAR_GARCOM, cb);
}

/** Dispara a abertura das promoções (quem hospeda o diálogo é a página do catálogo). */
export function abrirPromocoes(): void {
  window.dispatchEvent(new Event(EVT_ABRIR_PROMOCOES));
}

/** Inscreve um callback para abrir as promoções; retorna a função de cleanup. */
export function onAbrirPromocoes(cb: () => void): () => void {
  window.addEventListener(EVT_ABRIR_PROMOCOES, cb);
  return () => window.removeEventListener(EVT_ABRIR_PROMOCOES, cb);
}

/** Dispara a abertura da conta da mesa/comanda. */
export function abrirConta(): void {
  window.dispatchEvent(new Event(EVT_ABRIR_CONTA));
}

/** Inscreve um callback para abrir a conta; retorna a função de cleanup. */
export function onAbrirConta(cb: () => void): () => void {
  window.addEventListener(EVT_ABRIR_CONTA, cb);
  return () => window.removeEventListener(EVT_ABRIR_CONTA, cb);
}

/**
 * Dispara a configuração local do aparelho (totem ou tablet). Quem chama é o
 * "Sobre" (SobreCardapio), único caminho desde que os gestos secretos foram
 * removidos: o que protege é o PIN, validado no servidor, não o segredo.
 */
export function abrirConfigDispositivo(): void {
  window.dispatchEvent(new Event(EVT_ABRIR_CONFIG));
}

/** Inscreve um callback para abrir a configuração do aparelho; retorna o cleanup. */
export function onAbrirConfigDispositivo(cb: () => void): () => void {
  window.addEventListener(EVT_ABRIR_CONFIG, cb);
  return () => window.removeEventListener(EVT_ABRIR_CONFIG, cb);
}
